import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Bot, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

export const STATUS_META = {
  new: { label: "New", cls: "text-cyan-300 border-cyan-500/50" },
  investigating: { label: "Investigating", cls: "text-amber-300 border-amber-500/50" },
  waiting_on_frederick: { label: "Waiting on Frederick", cls: "text-rose-300 border-rose-500/50" },
  ready_for_test: { label: "Ready for Test", cls: "text-violet-300 border-violet-500/50" },
  completed: { label: "Completed", cls: "text-emerald-300 border-emerald-500/50" },
  blocked: { label: "Blocked", cls: "text-red-400 border-red-600/60" },
};

const STRUCTURED_FIELDS = [
  ["investigated", "What was investigated"],
  ["root_cause", "Root cause"],
  ["files_affected", "Files/components affected"],
  ["changes", "Changes made or proposed"],
  ["tests", "Tests performed"],
  ["env_status", "Preview/Production status"],
  ["risks", "Remaining risks"],
];

const inputCls = "bg-black/40 border border-white/10 rounded-none text-sm px-3 py-2 text-zinc-200 w-full";

function StructuredBlock({ s }) {
  return (
    <div className="border border-cyan-800/40 bg-cyan-950/10 p-3 mt-2 space-y-2" data-testid="neo-structured-response">
      <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 flex items-center gap-1.5"><Bot size={11} /> Neo engineering response</div>
      {STRUCTURED_FIELDS.map(([k, label]) => {
        const v = s[k];
        if (!v || (Array.isArray(v) && v.length === 0)) return null;
        return (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="text-xs text-zinc-200 whitespace-pre-wrap">{Array.isArray(v) ? v.join("\n") : v}</div>
          </div>
        );
      })}
      {s.needs_frederick_approval && (
        <div className="text-xs text-rose-300 flex items-center gap-1.5" data-testid="neo-needs-approval">
          <ShieldAlert size={12} /> Frederick's approval required before the next step.
        </div>
      )}
    </div>
  );
}

export default function NeoTicketThread({ ticketId, me, onBack }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showEvents, setShowEvents] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/neo/tickets/${ticketId}`);
      setData(d);
    } catch { toast.error("Could not load ticket."); }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading ticket…</div>;
  const t = data.ticket;
  const sm = STATUS_META[t.status] || STATUS_META.new;

  const setStatus = async (status) => {
    try {
      await api.post(`/admin/neo/tickets/${ticketId}/status`, { status });
      toast.success(`Status: ${STATUS_META[status].label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed."); }
  };

  const reply = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/neo/tickets/${ticketId}/messages`, { body });
      setBody("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reply failed."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="neo-ticket-thread">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Button size="sm" onClick={onBack} className="rounded-none text-xs bg-transparent border border-white/15 text-zinc-300" data-testid="neo-thread-back">
          <ArrowLeft size={13} /> Back
        </Button>
        <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${sm.cls}`} data-testid="neo-thread-status">{sm.label}</span>
        <div className="text-sm font-medium flex-1 min-w-0 truncate">{t.title}</div>
        <select value={t.status} onChange={(e) => setStatus(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-none text-xs px-2 py-1.5 text-zinc-200"
          data-testid="neo-thread-status-select">
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>

      <div className="card-surface p-4 mb-3">
        <div className="text-xs text-zinc-500 mb-2">
          {t.priority} · {t.area} · {t.environment} · {t.request_type === "diagnostic" ? "diagnostic-only" : "implementation requested"} ·
          opened by {t.created_by?.display_name} · {new Date(t.created_at).toLocaleString()}
        </div>
        <div className="text-sm text-zinc-200 whitespace-pre-wrap">{t.description}</div>
        {(t.attachments || []).length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {t.attachments.map((a) => (
              <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="block">
                <img src={a.url} alt={a.name || "attachment"} className="h-20 border border-white/10 object-cover" />
              </a>
            ))}
          </div>
        )}
        {t.diagnostics_snapshot && (
          <details className="mt-3">
            <summary className="text-xs text-cyan-400 cursor-pointer" data-testid="neo-thread-diag-toggle">Attached Live diagnostics snapshot</summary>
            <pre className="text-[11px] text-zinc-400 bg-black/40 border border-white/10 p-3 mt-2 overflow-x-auto max-h-72 overflow-y-auto">
              {JSON.stringify(t.diagnostics_snapshot, null, 2)}
            </pre>
          </details>
        )}
      </div>

      <div className="space-y-2 mb-3">
        {(data.messages || []).map((m) => (
          <div key={m.id} className="card-surface p-3" data-testid={`neo-msg-${m.id}`}>
            <div className="text-xs text-zinc-500 mb-1">
              {m.author?.display_name}{m.author?.is_neo ? " · Neo (engineering)" : m.author?.is_owner ? " (owner)" : m.author?.admin_scope === "technical" ? " (tech admin)" : ""} · {new Date(m.created_at).toLocaleString()}
            </div>
            <div className="text-sm text-zinc-200 whitespace-pre-wrap">{m.body}</div>
            {(m.attachments || []).length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {m.attachments.map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt="attachment" className="h-16 border border-white/10 object-cover" /></a>
                ))}
              </div>
            )}
            {m.structured && <StructuredBlock s={m.structured} />}
          </div>
        ))}
      </div>

      <div className="card-surface p-3 space-y-2">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
          placeholder="Reply in this thread…" className={`${inputCls} resize-y`} data-testid="neo-reply-body" />
        <div className="flex justify-end">
          <Button onClick={reply} disabled={sending || !body.trim()} className="btn-crimson rounded-none text-xs" data-testid="neo-reply-send">
            {sending ? "Sending…" : "Reply"}
          </Button>
        </div>
      </div>

      <button onClick={() => setShowEvents((v) => !v)} className="text-xs text-zinc-500 mt-3 hover:text-zinc-300" data-testid="neo-thread-events-toggle">
        {showEvents ? "Hide" : "Show"} activity timeline ({(t.events || []).length})
      </button>
      {showEvents && (
        <div className="mt-2 space-y-1">
          {(t.events || []).slice().reverse().map((e, i) => (
            <div key={i} className="text-xs text-zinc-500">
              {new Date(e.ts).toLocaleString()} — @{e.actor_handle} · {e.action}{e.detail ? ` · ${e.detail}` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
